/**
 * Shared Blog credits sync for dashboard + approval controls.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useLoadUser } from "../../../Utils/customHooks/useLoadUser";
import { fetchAllBlogPostsForDashboard } from "../../../Utils/SupabaseServices/BlogDatabase";
import {
  canSpendGenerationCredits,
  canSpendPublishCredit,
  generationCreditCost,
  hasActiveBlogPlan,
  isBlogCreditsEnforced,
  isBlogPublishingAllowed,
  reconcileBlogCreditsFromPosts,
  recordGenerationSpend,
  recordPublishSpend,
  syncBlogCreditsWithStripe,
  type BlogCreditReconcilePost,
  type BlogCreditSpendSource,
  type BlogCreditsState,
  type BlogGenerationSpendKind,
} from "../../../Utils/SupabaseServices/blogCredits";
import { useTemplateSettings } from "../MasterTemplateEditor/useTemplateSettings";
import type { Tweaks } from "../MasterTemplateEditor/types";

/** Optional shared template-settings session (avoids dual-fetch overwrite races). */
export type BlogCreditsSharedSettings = {
  tweaks: Pick<Tweaks, "blogCredits"> | Tweaks;
  loading: boolean;
  applyTweaksAndSaveNow: (patch: Partial<Tweaks>) => Promise<boolean>;
};

export type SpendGenerationOptions = {
  quantity?: number;
  source?: BlogCreditSpendSource;
  blogIds?: Array<string | number | null | undefined>;
  titles?: Array<string | null | undefined>;
};

export type SpendPublishOptions = {
  source?: BlogCreditSpendSource;
  blogId?: string | number | null;
  title?: string | null;
};

export function useBlogCredits(
  userId?: string,
  username?: string,
  sharedSettings?: BlogCreditsSharedSettings,
) {
  const { isAdmin } = useAuth();
  const { data: loadedUser, isLoading: userLoading } = useLoadUser(username);
  const userData =
    loadedUser && loadedUser !== false ? loadedUser : undefined;
  // When a parent already owns template settings, skip a second fetch/persist
  // session — otherwise cadence saves can revive a stale blogCredits ledger.
  const internal = useTemplateSettings(sharedSettings ? undefined : userId);
  const tweaks = sharedSettings?.tweaks ?? internal.tweaks;
  const loading = sharedSettings?.loading ?? internal.loading;
  const applyTweaksAndSaveNow =
    sharedSettings?.applyTweaksAndSaveNow ?? internal.applyTweaksAndSaveNow;
  const [local, setLocal] = useState<BlogCreditsState | null>(null);
  const didMonthSync = useRef(false);
  const didReconcile = useRef(false);

  // Don't treat "no plan" as locked until settings + user (stripe_detail) have loaded.
  const ready = !loading && !(username && userLoading);

  const synced = useMemo(
    () => syncBlogCreditsWithStripe(local ?? tweaks?.blogCredits, userData),
    [local, tweaks?.blogCredits, userData],
  );
  const creditsRef = useRef(synced);
  creditsRef.current = synced;

  const persist = useCallback(
    async (next: BlogCreditsState) => {
      creditsRef.current = next;
      setLocal(next);
      const ok = await applyTweaksAndSaveNow({ blogCredits: next });
      if (!ok) setLocal(null);
      return ok;
    },
    [applyTweaksAndSaveNow],
  );

  // Persist Stripe month reset / plan detection once per mount when settings load.
  useEffect(() => {
    if (loading || !userId || didMonthSync.current) return;
    const fromTweaks = syncBlogCreditsWithStripe(tweaks?.blogCredits, userData);
    const raw = tweaks?.blogCredits as BlogCreditsState | undefined;
    const drifted =
      fromTweaks.plan !== (raw?.plan ?? null) ||
      fromTweaks.generationRemaining !== (raw?.generationRemaining ?? 0) ||
      fromTweaks.publishRemaining !== (raw?.publishRemaining ?? 0) ||
      fromTweaks.periodStart !== (raw?.periodStart ?? null) ||
      (fromTweaks.usage?.length || 0) !== (raw?.usage?.length || 0);
    if (drifted && fromTweaks.plan) {
      didMonthSync.current = true;
      void persist(fromTweaks);
    } else if (fromTweaks.plan || raw?.plan) {
      didMonthSync.current = true;
    }
  }, [loading, userId, tweaks?.blogCredits, userData, persist]);

  const enforced = isBlogCreditsEnforced(synced, userData);
  const publishingAllowed = isBlogPublishingAllowed({
    isAdmin: Boolean(isAdmin),
    credits: synced,
    userData,
  });
  const hasPlan = hasActiveBlogPlan(synced, userData);

  const assertGeneration = useCallback(
    (kind: BlogGenerationSpendKind, quantity = 1) => {
      const units = Math.max(1, Math.floor(quantity));
      const cost = generationCreditCost(kind) * units;
      return canSpendGenerationCredits(creditsRef.current, cost, {
        isAdmin: Boolean(isAdmin),
        userData,
      });
    },
    [isAdmin, userData],
  );

  const spendGeneration = useCallback(
    async (
      kind: BlogGenerationSpendKind,
      quantityOrOpts: number | SpendGenerationOptions = 1,
    ) => {
      const opts: SpendGenerationOptions =
        typeof quantityOrOpts === "number"
          ? { quantity: quantityOrOpts }
          : quantityOrOpts || {};
      const units = Math.max(1, Math.floor(opts.quantity ?? 1));
      const cost = generationCreditCost(kind) * units;
      const check = canSpendGenerationCredits(creditsRef.current, cost, {
        isAdmin: Boolean(isAdmin),
        userData,
      });
      if (!check.ok) return check;

      const { credits: next, recorded } = recordGenerationSpend(
        creditsRef.current,
        {
          kind,
          quantity: units,
          source: opts.source || "dashboard_compose",
          isAdmin: Boolean(isAdmin),
          blogIds: opts.blogIds,
          titles: opts.titles,
        },
      );
      // Free regen / already-tracked ids / admin still persist when new rows appear
      // so the ledger stays complete. Skip write if nothing changed.
      if (recorded === 0 && next === creditsRef.current) {
        return { ok: true as const };
      }
      if (
        recorded === 0 &&
        next.generationRemaining === creditsRef.current.generationRemaining &&
        next.usage.length === creditsRef.current.usage.length
      ) {
        return { ok: true as const };
      }
      const ok = await persist(next);
      return ok
        ? { ok: true as const }
        : { ok: false as const, message: "Couldn't update generation credits." };
    },
    [isAdmin, userData, persist],
  );

  const spendPublish = useCallback(
    async (opts?: SpendPublishOptions) => {
      const check = canSpendPublishCredit(creditsRef.current, {
        isAdmin: Boolean(isAdmin),
        userData,
      });
      if (!check.ok) return check;
      const { credits: next, recorded } = recordPublishSpend(creditsRef.current, {
        source: opts?.source || "editor_publish",
        isAdmin: Boolean(isAdmin),
        blogId: opts?.blogId,
        title: opts?.title,
      });
      if (!recorded) return { ok: true as const };
      const ok = await persist(next);
      return ok
        ? { ok: true as const }
        : { ok: false as const, message: "Couldn't update publishing credits." };
    },
    [isAdmin, userData, persist],
  );

  const reconcileFromPosts = useCallback(
    async (posts: BlogCreditReconcilePost[]) => {
      if (!posts.length) {
        return { ok: true as const, generationAdded: 0, publishAdded: 0 };
      }
      const result = reconcileBlogCreditsFromPosts(creditsRef.current, posts, {
        isAdmin: Boolean(isAdmin),
      });
      if (result.generationAdded === 0 && result.publishAdded === 0) {
        return {
          ok: true as const,
          generationAdded: 0,
          publishAdded: 0,
        };
      }
      const ok = await persist(result.credits);
      return ok
        ? {
            ok: true as const,
            generationAdded: result.generationAdded,
            publishAdded: result.publishAdded,
          }
        : {
            ok: false as const,
            message: "Couldn't reconcile blog credits.",
            generationAdded: 0,
            publishAdded: 0,
          };
    },
    [isAdmin, persist],
  );

  // Backfill ledger for cron / scheduled gens that never hit a client spend.
  useEffect(() => {
    if (!ready || !userId || !hasPlan || didReconcile.current) return;
    didReconcile.current = true;
    let cancelled = false;
    void (async () => {
      const posts = await fetchAllBlogPostsForDashboard(userId, 120);
      if (cancelled || !posts.length) return;
      await reconcileFromPosts(posts);
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, userId, hasPlan, reconcileFromPosts]);

  return {
    loading,
    ready,
    userData,
    isAdmin: Boolean(isAdmin),
    credits: synced,
    enforced,
    hasPlan,
    publishingAllowed,
    persist,
    assertGeneration,
    spendGeneration,
    spendPublish,
    reconcileFromPosts,
  };
}
